import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, format, startOfDay, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { DailyPunctuality, DailyShiftDetail } from '@/hooks/useCaregiverDetail';

interface Props {
  data: DailyPunctuality[];
  periodDays: number;
  dailyShifts?: Record<string, DailyShiftDetail[]>;
}

type CellStatus = 'on_time' | 'late' | 'no_shift';

interface DayCell {
  date: Date;
  iso: string;
  status: CellStatus;
  shifts: DailyShiftDetail[];
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function CheckinPunctualityCalendar({ data, periodDays, dailyShifts = {} }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pt-BR' ? ptBR : undefined;

  const { cells, leadingEmpty } = useMemo(() => {
    const map = new Map<string, number | null>();
    data.forEach((d) => map.set(d.date, d.checkin_pct));

    const today = startOfDay(new Date());
    const start = subDays(today, periodDays - 1);

    const days: DayCell[] = [];
    for (let i = 0; i < periodDays; i++) {
      const date = addDays(start, i);
      const iso = toIsoDate(date);
      const pct = map.has(iso) ? map.get(iso)! : null;
      const shifts = dailyShifts[iso] || [];
      let status: CellStatus = 'no_shift';
      if (pct !== null) status = pct === 100 ? 'on_time' : 'late';
      else if (shifts.length > 0) status = 'late'; // shifts without template still count as activity
      days.push({ date, iso, status, shifts });
    }

    const leadingEmpty = days[0].date.getDay();
    return { cells: days, leadingEmpty };
  }, [data, periodDays, dailyShifts]);

  const weekdayHeaders = useMemo(() => {
    const base = new Date(2024, 0, 7);
    return Array.from({ length: 7 }, (_, i) =>
      format(addDays(base, i), 'eeeeee', { locale }).toUpperCase()
    );
  }, [locale]);

  const statusClass = (s: CellStatus, hasShifts: boolean) => {
    const interactive = hasShifts ? 'cursor-pointer' : 'cursor-default';
    const base =
      s === 'on_time'
        ? 'bg-success/20 text-success hover:bg-success/30'
        : s === 'late'
        ? 'bg-destructive/20 text-destructive hover:bg-destructive/30'
        : 'bg-muted text-muted-foreground/60';
    return `${base} ${interactive}`;
  };

  const statusLabel = (s: CellStatus) =>
    s === 'on_time'
      ? t('caregiverDetail.legendOnTime')
      : s === 'late'
      ? t('caregiverDetail.legendLate')
      : t('caregiverDetail.legendNoShift');

  const formatTime = (iso: string) => format(new Date(iso), 'HH:mm');

  const formatLate = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-7 gap-1">
        {weekdayHeaders.map((label, i) => (
          <div
            key={i}
            className="text-[10px] font-medium text-muted-foreground text-center pb-1"
          >
            {label}
          </div>
        ))}
      </div>

      <TooltipProvider delayDuration={150}>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: leadingEmpty }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}

          {cells.map((cell) => {
            const cellInner = (
              <div
                className={cn(
                  'aspect-square rounded-md flex items-center justify-center text-xs font-medium transition-colors',
                  statusClass(cell.status, cell.shifts.length > 0)
                )}
              >
                {cell.date.getDate()}
              </div>
            );

            if (cell.shifts.length === 0) {
              return (
                <Tooltip key={cell.iso}>
                  <TooltipTrigger asChild>{cellInner}</TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <div className="font-medium">
                        {format(cell.date, 'PPP', { locale })}
                      </div>
                      <div className="text-muted-foreground">{statusLabel(cell.status)}</div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            }

            return (
              <Popover key={cell.iso}>
                <PopoverTrigger asChild>{cellInner}</PopoverTrigger>
                <PopoverContent className="w-72 p-3" align="center">
                  <div className="space-y-2">
                    <div className="border-b pb-2">
                      <div className="text-sm font-semibold">
                        {format(cell.date, 'PPP', { locale })}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('caregiverDetail.shiftsOnDay')} ({cell.shifts.length})
                      </div>
                    </div>

                    <div className="space-y-3 max-h-72 overflow-y-auto">
                      {cell.shifts.map((s) => (
                        <div
                          key={s.shift_id}
                          className="text-xs space-y-1 border-l-2 pl-2"
                          style={{
                            borderColor:
                              s.is_on_time === false
                                ? 'hsl(var(--destructive))'
                                : s.is_on_time === true
                                ? 'hsl(var(--success))'
                                : 'hsl(var(--muted-foreground))',
                          }}
                        >
                          <div className="font-medium text-foreground truncate">
                            {s.patient_nickname}
                          </div>
                          <div className="flex justify-between text-muted-foreground">
                            <span>{t('caregiverDetail.checkInLabel')}</span>
                            <span className="font-mono text-foreground">
                              {formatTime(s.check_in_at)}
                            </span>
                          </div>
                          <div className="flex justify-between text-muted-foreground">
                            <span>{t('caregiverDetail.checkOutLabel')}</span>
                            <span className="font-mono text-foreground">
                              {s.check_out_at
                                ? formatTime(s.check_out_at)
                                : t('caregiverDetail.stillActive')}
                            </span>
                          </div>
                          {s.expected_start ? (
                            <div className="flex justify-between text-muted-foreground">
                              <span>{t('caregiverDetail.expectedStart')}</span>
                              <span className="font-mono text-foreground">
                                {s.expected_start.slice(0, 5)}
                              </span>
                            </div>
                          ) : (
                            <div className="text-muted-foreground italic">
                              {t('caregiverDetail.noTemplate')}
                            </div>
                          )}
                          {s.late_minutes !== null && s.late_minutes > 0 && (
                            <div className="flex justify-between">
                              <span className="text-destructive font-medium">
                                {t('caregiverDetail.lateBy')}
                              </span>
                              <span className="font-mono font-semibold text-destructive">
                                {formatLate(s.late_minutes)}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            );
          })}
        </div>
      </TooltipProvider>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-success/30" />
          {t('caregiverDetail.legendOnTime')}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-destructive/30" />
          {t('caregiverDetail.legendLate')}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-muted border border-border" />
          {t('caregiverDetail.legendNoShift')}
        </div>
      </div>
    </div>
  );
}
